import { Icon, Text, HStack, Stack, VStack } from '@genial/components';
import { Utils } from '@genial/utils';
import { Image } from 'expo-image';
import { Box, Pressable, useTheme } from 'native-base';
import React, { useMemo } from 'react';

import { TableContent } from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { usePrivatePensionAssetContext } from '../../../../../contexts/products/PrivatePension';
import { LateralBar } from '../../../components/LateralBar';
import { privatePensionTableHead } from '../constants';
import { ListLoadingItemTablet } from '../ListLoadingItem/tablet';

import { MinimumApplicationTooltip } from './components/MinimumApplicationTooltip';
import { MonthlyContributionTooltip } from './components/MonthlyContributionTooltip';

import { PrivatePensionItemTypes } from '.';

export const PrivatePensionItemTablet: React.FC<PrivatePensionItemTypes.Props> = ({ offer }) => {
  const { setModalOpen, setOfferSelected } = usePrivatePensionAssetContext();
  const { infoGetter, selectedTab, accountId } = useGlobalAssetContext();
  const theme = useTheme();

  const { Button, isSelected, forceLoading } = infoGetter(
    'privatePension',
    offer,
    'privatePensionId'
  );

  const onOpenDetailsModal = () => {
    if (!accountId) {
      return;
    }

    setModalOpen(true);
    setOfferSelected({ ...offer, accountId });
  };

  const bottomInfos = useMemo(
    () => [
      {
        label: privatePensionTableHead[2].title,
        value: <Text color="neutral.dark">{offer.productTypeNormalized}</Text>,
      },
      {
        label: privatePensionTableHead[3].title,
        value: <Text color="neutral.dark">{offer.monthlyRentabilityNormalized}</Text>,
      },
      {
        label: privatePensionTableHead[4].title,
        value: <Text color="neutral.dark">{offer.annualRentabilityNormalized}</Text>,
      },
    ],
    [offer]
  );

  const bottomInfos2 = useMemo(
    () => [
      {
        label: privatePensionTableHead[5].title,
        value: <Text color="neutral.dark">{offer.twelveMonthsRentabilityNormalized}</Text>,
      },
      {
        label: privatePensionTableHead[6].title,
        value: (
          <Box flexDirection="row" style={{ gap: Utils.Token.ToInt(theme.space, 'stack.quarck') }}>
            <MinimumApplicationTooltip
              minimumApplicationNormalized={offer.minimumApplicationNormalized}
              monthlyMinimumApplicationNormalized={offer.monthlyMinimumApplicationNormalized}
            />
            <Text color="neutral.dark">{offer.minimumApplicationNormalized}</Text>
          </Box>
        ),
      },
      {
        label: privatePensionTableHead[7].title,
        value: (
          <Box flexDirection="row" style={{ gap: Utils.Token.ToInt(theme.space, 'stack.quarck') }}>
            <MonthlyContributionTooltip
              minimumApplicationNormalized={offer.minimumApplicationNormalized}
              monthlyMinimumApplicationNormalized={offer.monthlyMinimumApplicationNormalized}
            />
            <Text color="neutral.dark">{offer.monthlyMinimumApplicationNormalized}</Text>
          </Box>
        ),
      },
    ],
    [offer, theme.space]
  );

  if (forceLoading) {
    return <ListLoadingItemTablet />;
  }

  return (
    <TableContent direction="row" space={0} p={0} px="inline.xxxs" isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius="sm"
        borderBottomLeftRadius="sm"
      />
      <VStack padding="inset.sm" space="stack.xxxs" w="full">
        <HStack space="inline.quarck" alignItems="center" justifyContent="space-between">
          <Text color="neutral.dark">{offer.productNameNormalized}</Text>
          <HStack space="inline.quarck" alignItems="center">
            <Image source={{ uri: offer.managerImage }} style={{ width: 140, height: 50 }} />
            <Pressable onPress={onOpenDetailsModal}>
              <Icon name="Info" size="xs" />
            </Pressable>
          </HStack>
        </HStack>
        <HStack justifyContent="space-between">
          {bottomInfos.map(({ label, value }) => (
            <VStack maxWidth="24%" minWidth="24%" space="stack.quarck" key={label}>
              <Text color="neutral.black" variant="semibold">
                {label}
              </Text>
              {value}
            </VStack>
          ))}
          <Stack alignSelf="flex-end" maxWidth="15%" minWidth="15%"></Stack>
        </HStack>
        <HStack justifyContent="space-between">
          {bottomInfos2.map(({ label, value }) => (
            <VStack maxWidth="24%" minWidth="24%" space="stack.quarck" key={label}>
              <Text color="neutral.black" variant="semibold">
                {label}
              </Text>
              {value}
            </VStack>
          ))}
          <Stack alignSelf="flex-end" maxWidth="15%" minWidth="15%">
            <Button
              id={selectedTab.typeGetterId}
              offer={offer}
              productContextConstant={selectedTab}
            />
          </Stack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
